import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useRequisicaoServicosProdutosListaViewModel } from "../../viewModels/requisicao_servicos_produtos_lista_view_model";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateRequisicaoServicosProdutosColumnDefinition } from "./columns";
import { RequisicaoCompraLista } from "../../../domain/models/requisicao_compra_lista";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";
import { SendMailModal } from "../../../../../common/components/send_email_modal";
import { GridPinnedColumnFields, GRID_CHECKBOX_SELECTION_FIELD } from "@mui/x-data-grid";

import AlertError from "../../../../../common/components/alert_error";
import RefreshButton from "../../../../../common/components/refresh_button";
import PrimaryButton from "../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * RequisicaoServicosProdutosLista
 */

export const RequisicaoServicosProdutosLista = () => {
  const viewModel = useRequisicaoServicosProdutosListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateRequisicaoServicosProdutosColumnDefinition({
      onActionEditar: viewModel.handleRequisicaoProdutoServicoModalOpenState,
      onActionDelete: viewModel.handleRequisicaoProdutoServicoDeleteModalOpenState,
      onActionExportar: viewModel.handleRequisicaoProdutoServicoExportar,
      onActionEmail: viewModel.handleEmailOpenState,
      onActionCopy: viewModel.handleCopyRequisicaoProdutoServicoSubmit,
    });
  }, [
    viewModel.handleRequisicaoProdutoServicoModalOpenState,
    viewModel.handleRequisicaoProdutoServicoDeleteModalOpenState,
    viewModel.handleRequisicaoProdutoServicoExportar,
    viewModel.handleEmailOpenState,
    viewModel.handleCopyRequisicaoProdutoServicoSubmit,
  ]);

  const pinnedColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.requisicoes.length > 0 ?
      {
        left: [
          GRID_CHECKBOX_SELECTION_FIELD,
        ],
        right: [
          'idRequisicaoCompra',
        ],
      } : {
      };
  }, [
    viewModel.requisicoes,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[210px]">
                <PrimaryButton
                  buttonColor="yellow"
                  startIcon={<DynamicIcon nameIcon="CheckBox" size={25} className="text-black" />}
                  onMouseDown={e => {
                    viewModel.handleFinalizarLoteSubmit()
                    e.preventDefault();
                  }}>
                  Finalizar Selecionados
                </PrimaryButton>
              </div>

              <div className="w-[115px]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    if (e.button === 1) {
                      viewModel.handleRequisicaoProdutoServicoModalOpenState(null, true);
                    } else {
                      viewModel.handleRequisicaoProdutoServicoModalOpenState(null, e.ctrlKey);
                    }
                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Compras
          </Link>
          <Typography className="text-primary-green">
            Requisição de Produtos e Serviços
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-2">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-3xl font-medium text-black dark:text-white">Requisição de Produtos e Serviços</h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>
          </div>
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  nroRequisicao={viewModel.filters.nroRequisicao}
                  dataRequisicaoStart={viewModel.filters.dataRequisicaoStart}
                  dataRequisicaoEnd={viewModel.filters.dataRequisicaoEnd}
                  centroCustos={viewModel.filters.centroCustos}
                  solicitantes={viewModel.filters.solicitantes}
                  status={viewModel.filters.status}
                  produtosServicos={viewModel.filters.produtosServicos}
                  dataPrevistaStart={viewModel.filters.dataPrevistaStart}
                  dataPrevistaEnd={viewModel.filters.dataPrevistaEnd}
                  centroCustoDropdown={viewModel.centroCustoDropdown}
                  funcionarioDropdown={viewModel.funcionarioDropdown}
                  produtosServicosDropdown={viewModel.produtosServicosDropdown}
                  onNroRequisicaoChange={viewModel.handleNroRequisicaoChange}
                  onDataRequisicaoChange={viewModel.handleDataRequisicaoChange}
                  onCentroCustosChange={viewModel.handleCentroCustosChange}
                  onSolicitantesChange={viewModel.handleSolicitantesChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onProdutosServicosChange={viewModel.handleProdutosServicosChange}
                  onDataPrevistaChange={viewModel.handleDataPrevistaChange}
                  onClearFilter={viewModel.handleClearFilter}
                />

                <Stack spacing={0}>
                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                  <StyledDataGrid
                    tableName="RequisicaoServicosProdutos_lista"
                    disableColumnMenu={false}
                    getRowId={(row: RequisicaoCompraLista) => row.idRequisicaoCompra}
                    getRowHeight={() => 'auto'}
                    textSize="pequeno"
                    checkboxSelection={true}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    disableRowSelectionOnClick={true}
                    rows={viewModel.requisicoes}
                    rowCount={viewModel.requisicoes.length}
                    pinnedColumns={pinnedColumns}
                    rowSelectionModel={viewModel.rowSelectionModel}
                    onRowSelectionModelChange={viewModel.handleRowSelectionModel}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <DeleteDialog
        open={viewModel.requisicaoProdutoServicoDeleteModal}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleRequisicaoProdutoServicoDeleteModalOpenState(null, false)}
        onDelete={() => viewModel.handleRequisicaoProdutoServicoDeleteSubmit()}
      />

      <SendMailModal
        open={viewModel.emailModal}
        loading={viewModel.loading}
        sendTo={viewModel.emailSendTo}
        subject={viewModel.emailSubject}
        attachment={viewModel.emailAttachment}
        body={viewModel.emailBody}
        onClose={() => viewModel.handleEmailOpenState(null, false)}
        onSubmit={viewModel.handleEmailSubmit}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />

    </>
  );
}